import melanieBioPhoto from '../../assets/bio/Melanie-Ray-Photo.jpg';
import type { BoardMemberRecord } from '../types';

const retiredBoardMemberIds = new Set(['board-kim-gordon']);

export const seededBoardMembers: BoardMemberRecord[] = [
  {
    id: 'board-melanie-ray',
    user_id: null,
    name: 'Dr. Melanie I. Ray',
    board_title: "President / Founder, Victoria's Place",
    bio:
      "Dr. Melanie I. Ray is the president and founder of Victoria's Place. Her work as a mental health counselor, behavior therapist, and program director has always centered on helping children and families feel supported, seen, and encouraged.\n\nAs Victoria's mother, Melanie experienced firsthand how important family support, faith, early intervention, and strong community relationships can be. That lived experience shaped the vision for Victoria's Place and continues to guide its mission.\n\nShe remains committed to creating a loving, compassionate environment where children with Down Syndrome can build confidence, friendship, self-esteem, and joy.",
    photo_url: melanieBioPhoto,
    photo_alt: 'Dr. Melanie Ray holding Victoria',
    display_order: 1,
    is_visible: true,
    created_at: 1710000000000,
    updated_at: 1710000000000,
  },
];

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeBoardMember(member: BoardMemberRecord): BoardMemberRecord {
  const id = normalizeText(member.id) || `board-${Date.now().toString(36)}`;
  const isMelanieRay = id === 'board-melanie-ray';
  const originalName = normalizeText(member.name);
  const name =
    isMelanieRay && originalName && !/^dr\.\s/i.test(originalName)
      ? `Dr. ${originalName}`
      : originalName;
  const originalPhotoAlt = normalizeText(member.photo_alt);
  const photoAlt =
    isMelanieRay && originalPhotoAlt && !/^dr\.\s/i.test(originalPhotoAlt)
      ? `Dr. ${originalPhotoAlt}`
      : originalPhotoAlt || (name ? `${name} portrait` : '');
  const originalBio = typeof member.bio === 'string' ? member.bio.trim() : '';
  const bio =
    isMelanieRay && !/^dr\.\s+melanie/i.test(originalBio)
      ? originalBio.replace(/^melanie/i, 'Dr. Melanie')
      : originalBio;
  const displayOrder = Number.isFinite(member.display_order) ? Math.max(0, Number(member.display_order)) : 0;
  const createdAt = Number.isFinite(member.created_at) ? Number(member.created_at) : Date.now();
  const updatedAt = Number.isFinite(member.updated_at) ? Number(member.updated_at) : createdAt;

  return {
    ...member,
    id,
    user_id: normalizeText(member.user_id) || null,
    name,
    board_title: normalizeText(member.board_title) || 'Board Member',
    bio,
    photo_url: normalizeText(member.photo_url),
    photo_alt: photoAlt,
    display_order: displayOrder,
    is_visible: Boolean(member.is_visible),
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function normalizeBoardMembers(members: BoardMemberRecord[]): BoardMemberRecord[] {
  return members
    .filter((member) => !retiredBoardMemberIds.has(normalizeText(member.id)))
    .map((member) => normalizeBoardMember(member))
    .sort((left, right) => {
      if (left.display_order !== right.display_order) {
        return left.display_order - right.display_order;
      }

      return left.name.localeCompare(right.name);
    });
}

export function createBoardMemberDraft(overrides: Partial<BoardMemberRecord> = {}): BoardMemberRecord {
  const now = Date.now();

  return normalizeBoardMember({
    id: overrides.id ?? `board-${now.toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    user_id: overrides.user_id ?? null,
    name: overrides.name ?? '',
    board_title: overrides.board_title ?? 'Board Member',
    bio: overrides.bio ?? '',
    photo_url: overrides.photo_url ?? '',
    photo_alt: overrides.photo_alt ?? '',
    display_order: overrides.display_order ?? 0,
    is_visible: overrides.is_visible ?? true,
    created_at: overrides.created_at ?? now,
    updated_at: overrides.updated_at ?? now,
  });
}
